<template>
  <footer class="relative z-10 bg-white border-t border-gray-200 py-3">
    <!-- Flight Ideas discovery links (SEO internal linking) -->
    <div class="container mx-auto px-4 pb-3 mb-2 border-b border-gray-100">
      <div class="flex flex-wrap gap-x-4 gap-y-1 items-center">
        <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">{{ t('footer.flightIdeas') }}</span>
        <router-link
          v-for="link in ideaLinks"
          :key="link.path"
          :to="link.path"
          class="text-xs text-gray-500 hover:text-blue-600 transition-colors"
        >{{ link.label }}</router-link>
      </div>
    </div>

    <div class="container mx-auto px-4 flex flex-wrap items-center justify-between gap-y-2">
      <p class="text-xs text-gray-500">
        &copy; {{ currentYear }} ProperFlight
      </p>

      <!-- Sign up CTA – hidden for authenticated users -->
      <button
        v-if="!authStore.isAuthenticated"
        @click="authStore.openAuthModal('signup')"
        class="text-gray-500 hover:text-gray-700 transition-colors text-xs"
      >
        <span class="font-bold text-blue-700">{{ t('footer.signUp') }}</span> {{ t('footer.signUpCta') }}
      </button>

      <nav class="flex items-center flex-wrap gap-x-1 gap-y-1">
        <router-link to="/" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.home') }}</router-link>
        <router-link to="/searches/basic" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.search') }}</router-link>
        <router-link to="/explore" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.explore') }}</router-link>
        <a href="/#faq-section" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.faq') }}</a>
        <router-link to="/privacy" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.privacy') }}</router-link>
        <router-link to="/cookies" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.cookies') }}</router-link>
        <router-link to="/terms" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.terms') }}</router-link>
        <button @click="consentStore.reopen()" class="text-gray-500 hover:text-gray-700 transition-colors text-xs px-2 py-2 rounded-md hover:bg-gray-50">{{ t('footer.manageCookies') }}</button>
        <div class="flex items-center ml-1 gap-1">
          <!-- Instagram -->
          <a
            href="https://www.instagram.com/properflight"
            target="_blank"
            rel="noopener noreferrer"
            class="p-2 text-gray-400 hover:text-pink-500 transition-colors rounded-md hover:bg-gray-50"
            aria-label="ProperFlight on Instagram"
          >
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.148 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.148-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.948-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <!-- X / Twitter -->
          <a
            href="https://x.com/properflight"
            target="_blank"
            rel="noopener noreferrer"
            class="p-2 text-gray-400 hover:text-gray-800 transition-colors rounded-md hover:bg-gray-50"
            aria-label="ProperFlight on X"
          >
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
        </div>
      </nav>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useConsentStore } from '@/stores/consentStore'
import { useI18n } from 'vue-i18n'

const { t, tm } = useI18n()
const currentYear = computed(() => new Date().getFullYear())
const authStore = useAuthStore()
const consentStore = useConsentStore()

const ideaLinks = computed(() => tm('footer.ideaLinks'))
</script>

<style scoped>
a, button {
  @apply transition-colors duration-300;
}
</style>
